import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { acceptInvitation, verifyInvitation } from '@/services/customer/invitation.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Trans, useTranslation } from 'react-i18next';
import { normalizeLanguageCode } from '@/lib/i18n/normalize-language';

const schema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((vals) => vals.password === vals.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type FormValues = z.infer<typeof schema>;

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function CustomerInvitationAcceptPage() {
  const params = useQueryParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const token = params.get('token') || '';
  const email = params.get('email') || '';
  const language = normalizeLanguageCode(i18n.language);

  const [apiError, setApiError] = useState<string | null>(null);

  const verifyQuery = useQuery({
    queryKey: ['invitation-verify', token, email],
    queryFn: () => verifyInvitation({ token, email }),
    enabled: !!token && !!email,
    retry: 1,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const acceptMut = useMutation({
    mutationFn: (values: FormValues) =>
      acceptInvitation({
        token,
        email,
        password: values.password,
        password_confirmation: values.confirmPassword,
        language,
      }),
    onSuccess: () => {
      const redirect = `/login?email=${encodeURIComponent(email)}`;
      navigate(redirect, { replace: true });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('customer.portal.invitation.errorFallback');
      setApiError(message);
    },
  });

  useEffect(() => {
    if (!token || !email) {
      setApiError(t('customer.portal.invitation.missingParams'));
    }
  }, [email, t, token]);

  const isLoading = verifyQuery.isPending || acceptMut.isPending;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('customer.portal.invitation.title')}</CardTitle>
          <CardDescription>
            {t('customer.portal.invitation.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {verifyQuery.isError && (
            <Alert className="mb-4" variant="destructive">
              <AlertDescription>
                {(verifyQuery.error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                  t('customer.portal.invitation.invalidLink')}
              </AlertDescription>
            </Alert>
          )}
          {apiError && (
            <Alert className="mb-4" variant="destructive">
              <AlertDescription>{apiError}</AlertDescription>
            </Alert>
          )}

          {verifyQuery.isPending ? (
            <div className="text-sm text-muted-foreground">
              {t('customer.portal.invitation.verifying')}
            </div>
          ) : verifyQuery.data ? (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((values) => acceptMut.mutate(values))}
                className="space-y-4"
              >
                <div className="text-sm text-muted-foreground -mt-2">
                  <Trans
                    i18nKey="customer.portal.invitation.invitationFor"
                    values={{ email }}
                    components={{ strong: <span className="font-medium" /> }}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customer.portal.invitation.passwordLabel')}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customer.portal.invitation.confirmPasswordLabel')}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {acceptMut.isPending
                    ? t('customer.portal.invitation.submitting')
                    : t('customer.portal.invitation.submitCta')}
                </Button>
              </form>
            </Form>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
